import { EditOutlined } from '@ant-design/icons';
import { Badge, Button, Form, List, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { CoolToKnow } from 'app/components/commons/CoolToKnow/CoolToKnow';
import type { ExperienceFormValues } from 'app/components/commons/Experience/ExperienceForm';
import { ExperienceForm } from 'app/components/commons/Experience/ExperienceForm';
import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { computeExperienceName } from 'app/components/commons/Experience/utils';
import type { OpeningHours } from 'app/components/commons/Hotel/Types';
import { Mode } from 'app/components/commons/Hotel/Types';
import type { FormOpeningHours } from 'app/components/commons/TimeRangeSection/utils';
import {
  formatDateRangeForAPI,
  formatOpeningHoursForAPI,
  parseDateRangeFromAPI,
  parseOpeningHoursFromAPI,
} from 'app/components/commons/TimeRangeSection/utils';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useHotelExperiences } from 'app/hooks/data/hotels/useHotelExperience';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { DateRange, Experience } from 'app/typings/builder/experience';
import { dayjs } from 'app/utils/dayjs';

import { HeaderWrapper } from '../HeaderWrapper/HeaderWrapper';

import './ExperiencesForm.scss';

type Props = {
  saveExperience: (experience: Experience) => void;
  handleNext: () => void;
  hotelId: number;
  isLoading?: boolean;
};

export const ExperiencesForm = ({
  saveExperience,
  handleNext,
  hotelId,
  isLoading = false,
}: Props) => {
  const [selectedExperience, setSelectedExperience] =
    useState<Experience | null>(null);

  const { data } = useHotelExperiences(hotelId, {
    includeStaycationExp: false,
  });
  const experiences = useMemo(
    () => (data ?? []) as unknown as Experience[],
    [data]
  );
  const experiencesCategoryIds = useMemo(
    () => experiences.map((experience) => experience.categoryId),
    [experiences]
  );
  const { data: allExperienceCategories = [] } = useExperiencesCategories();
  const experienceCategories = useMemo(
    () =>
      allExperienceCategories.filter((category) =>
        experiencesCategoryIds.includes(category.id)
      ),
    [allExperienceCategories, experiencesCategoryIds]
  );

  const findCategoryById = useCallback(
    (experience: Experience) => {
      const maybeCategory = experienceCategories.find(
        (category) => category.id === experience.categoryId
      );

      return maybeCategory;
    },
    [experienceCategories]
  );

  const handleClose = (resetFields?: () => void) => () => {
    setSelectedExperience(null);

    if (resetFields) {
      resetFields();
    }
  };

  const handleSave = (values: Experience) => {
    if (!selectedExperience) {
      return;
    }

    const formatted: Experience = {
      ...selectedExperience,
      ...values,
      openingHours: values.openingHours
        ? formatOpeningHoursForAPI(
            values.openingHours as unknown as FormOpeningHours
          )
        : null,
      time: values.time ? dayjs(values.time).format('HH:mm') : null,
      roomServiceAvailable: values.roomServiceAvailable || false,
      roomServiceIncluded: values.roomServiceIncluded || false,
      id: selectedExperience.id || 0,
      published:
        selectedExperience.published === undefined
          ? true
          : selectedExperience.published,
      spaceIds:
        values.spaceIds?.filter((s) => s !== NOT_APPLICABLE_SPACE_ID) || [],
      price: values.price || 0,
      coverPicture: values.pictures ? values.pictures[0] : undefined,
      // We take value from the API and not the form since it's not modified in the builder
      closingPeriods: formatDateRangeForAPI(
        parseDateRangeFromAPI(selectedExperience.closingPeriods)
      ) as unknown as DateRange[],
      isAlwaysIncludedWithNight: false,
    };

    saveExperience(formatted);

    setSelectedExperience(null);
  };

  const isExperienceFilled = (experience: Experience) =>
    !!experience.coverPicture;
  const areExperiencesFilled = experiences.every(isExperienceFilled);

  const [form] = Form.useForm();

  const formattedValues: Experience | undefined = useMemo(() => {
    if (!selectedExperience) {
      return undefined;
    }

    return {
      ...selectedExperience,
      spaceIds:
        selectedExperience.spaceIds.length === 0
          ? [NOT_APPLICABLE_SPACE_ID]
          : selectedExperience.spaceIds,
      openingHours: parseOpeningHoursFromAPI(
        selectedExperience.openingHours as OpeningHours | null | undefined
      ),
      pictures: selectedExperience.pictures,
      time: selectedExperience.time
        ? dayjs(selectedExperience.time as string, 'HH:mm')
        : null,
      closingPeriods: parseDateRangeFromAPI(
        selectedExperience.closingPeriods
      ) as unknown as DateRange[],
    };
  }, [selectedExperience]);

  useEffect(() => {
    if (!formattedValues) {
      return;
    }

    form.resetFields();
    form.setFieldsValue(formattedValues);
  }, [formattedValues, form]);

  const renderExperienceActionButton = (experience: Experience) => {
    if (isExperienceFilled(experience)) {
      return <EditOutlined onClick={() => setSelectedExperience(experience)} />;
    }

    return (
      <Button
        onClick={() => setSelectedExperience(experience)}
        className="fill-experience-button"
      >
        Fill
      </Button>
    );
  };

  const renderExperience = (experience: Experience) => (
    <List.Item className="experiences-form-list-item">
      <div>
        <Badge color={isExperienceFilled(experience) ? 'green' : 'orange'} />
        {computeExperienceName({
          ...experience,
          category: findCategoryById(experience),
        })}
      </div>

      {renderExperienceActionButton(experience)}
    </List.Item>
  );

  const renderFooter = (isFieldsTouched: boolean, submit: () => void) => (
    <>
      <Button key="cancel" onClick={handleClose()}>
        Cancel
      </Button>
      <Button
        key="submit"
        disabled={!isFieldsTouched}
        onClick={() => submit()}
        type="primary"
      >
        Save
      </Button>
    </>
  );

  return (
    <HeaderWrapper
      nextDisabled={!areExperiencesFilled}
      handleNext={handleNext}
      isLoading={isLoading}
    >
      <SCard
        title="Hotel activities"
        subtitle="Fill all the activity information"
        isExpanded
        key="experiences"
        className="experiences-form-list"
      >
        <List
          size="large"
          dataSource={experiences}
          renderItem={renderExperience}
        />

        {!!selectedExperience && (
          <FormLegacyRenderProp
            onFinish={handleSave}
            initialValues={formattedValues}
            form={form}
          >
            {(
              values,
              {
                resetFields,
                isFieldsTouched,
                submit,
                setFieldsValue,
                getFieldsError,
                validateFields,
              }
            ) => {
              const category = findCategoryById(selectedExperience);
              const experienceName = computeExperienceName({
                ...selectedExperience,
                category,
              });

              return (
                <Modal
                  title={experienceName}
                  width={800}
                  onCancel={handleClose(resetFields)}
                  footer={renderFooter(isFieldsTouched(), submit)}
                  visible
                  className="experiences-modal"
                  wrapClassName="hotel-builder-modal"
                >
                  {category && (
                    <ExperienceForm
                      isBuilder
                      title={experienceName}
                      values={values as unknown as ExperienceFormValues}
                      setFieldsValue={
                        setFieldsValue as unknown as (
                          newValues: Partial<ExperienceFormValues>
                        ) => void
                      }
                      resetFields={resetFields}
                      isFieldsTouched={isFieldsTouched}
                      submit={submit}
                      onCancel={handleClose}
                      mode={Mode.Edit}
                      experienceId={selectedExperience.id}
                      isFormValid={getFieldsError().every(
                        (item) => item.errors.length > 0
                      )}
                      validateFields={validateFields}
                    />
                  )}

                  <CoolToKnow />
                </Modal>
              );
            }}
          </FormLegacyRenderProp>
        )}
      </SCard>
    </HeaderWrapper>
  );
};
